import {
    IGroupConfigurationData,
    IPlan,
    IBenefit,
    IFlexibleField,
    IFlexibleFieldValue,
    IFlexibleFieldSet,
    INonStandard,
    INonStandardTemplate,
    IGroupStructure,
    IGroupNumber,
    IDepartment,
    ICESNumber,
    IAccountsReceivable,
    IFeesAndPremiums,
    IStopLoss,
    IComment,
    INotification,
    IPharmacyORE,
    IRef,
    IValue,
    IStringValue,
    INumberValue,
    IDateValue,
    IBooleanValue,
    INullableBooleanValue,
    IOptionSetValue,
    IEntityReferenceValue,
    IMultiselectOptionSetValue,
    TValue,
    TGuid,
    TOptionSet,
    ICategoryBase,
    EBenefitStatus,
    EGroupConfigurationStatus,
    EProduct,
    ECommentStatus,
    ITierLevel,
    ITemplateRelationship,
    ITemplateSectionRelationship,
} from "model/FlexibleAttributeModel";
import { IValidationMessage, EValidationSeverity } from "components/controls/ValidationView/ValidationView.type";

/**
 * Configuration options for validation behavior
 */
export interface ValidationOptions {
    /**
     * When true, validates that no extra attributes exist beyond schema definition
     * When false, ignores extra properties
     * Default: true
     */
    strict?: boolean;

    /**
     * When true, verifies that all IRef references resolve to existing entities in the data
     * When false, only validates IRef structure (id/name present)
     * Default: true
     */
    checkReferences?: boolean;

    /**
     * When true, recursively validates all nested structures
     * When false, validates only top-level structure
     * Default: true
     */
    validateAllLevels?: boolean;
}

/**
 * Internal context for tracking validation state
 */
interface ValidationContext {
    /**
     * Current path in the object hierarchy, e.g. "benefits[0].fields[2].value"
     */
    path: string;

    /**
     * Collected validation errors
     */
    errors: IValidationMessage[];

    /**
     * Map of entity references by ID for circular validation
     * Key: {entityType}:{id}, Value: entity name
     */
    refMap: Map<string, string>;

    /**
     * Track visited references to detect circular dependencies
     */
    visitedRefs: Set<string>;

    /**
     * Configuration options for this validation run
     */
    options: Required<ValidationOptions>;
}

/**
 * Main validator class for IGroupConfigurationData
 *
 * @example
 * // Basic validation with default options (strict=true, checkReferences=true)
 * const validator = new IGroupConfigurationDataValidator();
 * const errors = validator.validate(myData);
 *
 * @example
 * // Loose validation - ignore extra attributes and skip reference checks
 * const errors = validator.validate(myData, { strict: false, checkReferences: false });
 *
 * @example
 * // Check for schema compliance and reference integrity
 * const errors = validator.validate(myData, { strict: true, checkReferences: true });
 */
export class GroupConfigurationSchemaValidator {
    /**
     * Validate a complete IGroupConfigurationData object
     * @param data The data to validate
     * @param options Validation behavior options
     * @returns Array of IValidationMessage errors (empty if valid)
     */
    public validate(data: IGroupConfigurationData, options?: ValidationOptions): IValidationMessage[] {
        const mergedOptions: Required<ValidationOptions> = {
            strict: options?.strict ?? true,
            checkReferences: options?.checkReferences ?? true,
            validateAllLevels: options?.validateAllLevels ?? true,
        };

        const context: ValidationContext = {
            path: "root",
            errors: [],
            refMap: new Map(),
            visitedRefs: new Set(),
            options: mergedOptions,
        };

        // Type check: must be an object
        if (!data || typeof data !== "object" || Array.isArray(data)) {
            return [
                {
                    title: "Invalid data type",
                    type: EValidationSeverity.error,
                    description: "IGroupConfigurationData must be an object, not null or array",
                    value: data,
                },
            ];
        }

        // Validate root structure
        this.validateIGroupConfigurationData(data, context);

        // Build reference map for all entities (first pass)
        if (mergedOptions.checkReferences) {
            this.buildReferenceMap(data, context);
        }

        // Validate references (second pass)
        if (mergedOptions.checkReferences) {
            this.validateReferences(data, context);
        }

        return context.errors;
    }

    // ============= Root Level Validation =============

    /**
     * Validate IGroupConfigurationData root object
     */
    private validateIGroupConfigurationData(data: any, context: ValidationContext): void {
        // Required fields
        this.validateRequired(data, "id", "string", context);
        this.validateRequired(data, "name", "string", context);
        this.validateRequired(data, "iteration", "number", context);

        // Simple type fields
        this.validateFieldType(data, "bcbs_shortname", "string", context, true);
        this.validateFieldType(data, "appVersion", "string", context, true);
        this.validateFieldType(data, "emit", "boolean", context, true);

        // Value wrapper fields
        if (data.effectiveDate !== null && data.effectiveDate !== undefined) {
            this.validateIDateValue(data.effectiveDate, context, "effectiveDate");
        }
        if (data.operativeDate !== null && data.operativeDate !== undefined) {
            this.validateIDateValue(data.operativeDate, context, "operativeDate");
        }

        if (data.changeType !== null && data.changeType !== undefined) {
            this.validateIOptionSetValue(data.changeType, context, "changeType");
        }
        if (data.fundingType !== null && data.fundingType !== undefined) {
            this.validateIOptionSetValue(data.fundingType, context, "fundingType");
        }
        if (data.erisa !== null && data.erisa !== undefined) {
            this.validateIOptionSetValue(data.erisa, context, "erisa");
        }
        if (data.grandFathered !== null && data.grandFathered !== undefined) {
            this.validateIOptionSetValue(data.grandFathered, context, "grandFathered");
        }
        if (data.lineOfBusiness !== null && data.lineOfBusiness !== undefined) {
            this.validateIOptionSetValue(data.lineOfBusiness, context, "lineOfBusiness");
        }

        if (data.dualFunded !== null && data.dualFunded !== undefined) {
            this.validateIBooleanValue(data.dualFunded, context, "dualFunded");
        }
        if (data.MNIL !== null && data.MNIL !== undefined) {
            this.validateIBooleanValue(data.MNIL, context, "MNIL");
        }
        if (data.showInActives !== null && data.showInActives !== undefined) {
            this.validateIBooleanValue(data.showInActives, context, "showInActives");
        }
        if (data.reconciliation !== null && data.reconciliation !== undefined) {
            this.validateIBooleanValue(data.reconciliation, context, "reconciliation");
        }
        if (data.isRevision !== null && data.isRevision !== undefined) {
            this.validateIBooleanValue(data.isRevision, context, "isRevision");
        }

        // Status enum
        if (data.status !== null && data.status !== undefined) {
            this.validateIOptionSetValue(data.status, context, "status");
        }

        // Array fields
        if (data.fields !== undefined) {
            this.validateArray(data.fields, (item, ctx) => {
                this.validateIFlexibleField(item, ctx);
            }, "fields", context);
        }

        if (data.nonStandards !== undefined) {
            this.validateArray(data.nonStandards, (item, ctx) => {
                this.validateINonStandard(item, ctx, false);
            }, "nonStandards", context);
        }

        if (data.nonStandardTemplates !== undefined) {
            this.validateArray(data.nonStandardTemplates, (item, ctx) => {
                this.validateINonStandardTemplate(item, ctx);
            }, "nonStandardTemplates", context);
        }

        if (data.attributeSets !== undefined) {
            this.validateArray(data.attributeSets, (item, ctx) => {
                this.validateIFlexibleFieldSet(item, ctx);
            }, "attributeSets", context);
        }

        if (data.plans !== undefined) {
            this.validateArray(data.plans, (item, ctx) => {
                this.validateIPlan(item, ctx);
            }, "plans", context);
        }

        if (data.benefits !== undefined) {
            this.validateArray(data.benefits, (item, ctx) => {
                this.validateIBenefit(item, ctx);
            }, "benefits", context);
        }

        if (data.comments !== undefined) {
            this.validateArray(data.comments, (item, ctx) => {
                this.validateIComment(item, ctx);
            }, "comments", context);
        }

        if (data.claimsARs !== undefined) {
            this.validateArray(data.claimsARs, (item, ctx) => {
                this.validateIAccountsReceivable(item, ctx);
            }, "claimsARs", context);
        }

        if (data.feesAndPremiums !== undefined) {
            this.validateArray(data.feesAndPremiums, (item, ctx) => {
                this.validateIFeesAndPremiums(item, ctx);
            }, "feesAndPremiums", context);
        }

        if (data.stopLoss !== undefined) {
            this.validateArray(data.stopLoss, (item, ctx) => {
                this.validateIStopLoss(item, ctx);
            }, "stopLoss", context);
        }

        if (data.revisionTasks !== undefined) {
            this.validateArray(data.revisionTasks, (item, ctx) => {
                this.validateIEntityReferenceValue(item, ctx);
            }, "revisionTasks", context);
        }

        if (data.pharmacyORE !== undefined) {
            this.validateArray(data.pharmacyORE, (item, ctx) => {
                this.validateIPharmacyORE(item, ctx);
            }, "pharmacyORE", context);
        }

        if (data.notifications !== undefined) {
            this.validateArray(data.notifications, (item, ctx) => {
                this.validateINotification(item, ctx);
            }, "notifications", context);
        }

        // Complex objects
        if (data.groupStructure !== undefined && data.groupStructure !== null) {
            this.validateIGroupStructure(data.groupStructure, context);
        }

        if (data.releaseRef !== undefined && data.releaseRef !== null) {
            this.validateIRef(data.releaseRef, "releaseRef", context);
        }

        if (data.opportunity !== undefined && data.opportunity !== null) {
            this.validateIEntityReferenceValue(data.opportunity, context);
        }

        // Optional string fields
        if (data.releaseVersion !== undefined && data.releaseVersion !== null) {
            this.validateFieldType(data, "releaseVersion", "string", context, true);
        }

        if (data.groupCollectStatus !== undefined && data.groupCollectStatus !== null) {
            this.validateFieldType(data, "groupCollectStatus", "string", context, true);
        }

        // Audit object
        if (data.audit !== undefined && data.audit !== null) {
            if (typeof data.audit === "object" && data.audit.modifiedBy !== undefined) {
                this.validateIEntityReferenceValue(data.audit.modifiedBy, context);
            }

            this.validateStrictObjectFields(data.audit, ["modifiedBy"], context, "audit");
        }

        // Transformers object (optional flags)
        if (data.transformers !== undefined && data.transformers !== null) {
            if (typeof data.transformers === "object") {
                const savedPath = context.path;
                context.path = `${savedPath}.transformers`;
                const validTransformers = [
                    "Sprint17", "Sprint18", "Sprint21", "Sprint22", "Sprint24", "Sprint30",
                    "Sprint33", "Sprint33PatchTransformer", "Sprint34", "Sprint35", "Sprint40", "Sprint43"
                ];
                for (const [key, value] of Object.entries(data.transformers)) {
                    if (!validTransformers.includes(key)) {
                        this.addError(key, `Unknown transformer: ${key}`, context);
                    }
                    if (value !== true && value !== undefined) {
                        this.addError(key, "Transformer value must be true or undefined", context);
                    }
                }
                context.path = savedPath;
            }
        }

        // Strict mode: check for unknown fields
        if (context.options.strict) {
            const allowedFields = [
                "id", "name", "iteration", "bcbs_shortname", "effectiveDate", "operativeDate",
                "changeType", "fundingType", "dualFunded", "erisa", "grandFathered", "MNIL",
                "lineOfBusiness", "fields", "nonStandards", "nonStandardTemplates", "attributeSets",
                "plans", "benefits", "comments", "groupStructure", "claimsARs", "feesAndPremiums",
                "stopLoss", "showInActives", "reconciliation", "pharmacyORE", "status", "releaseRef",
                "releaseVersion", "opportunity", "groupCollectStatus", "isRevision", "revisionTasks",
                "notifications", "appVersion", "transformers", "audit", "emit", "deactivated"
            ];
            this.validateNoExtraFields(data, allowedFields, context);
        }
    }

    // ============= Category Base Type Validation =============

    /**
     * Validate ICategoryBase structure (shared by Benefit, AR, Fees, StopLoss)
     */
    private validateICategoryBase(data: any, context: ValidationContext, additionalAllowedFields: string[] = []): void {
        this.validateRequired(data, "id", "string", context);
        this.validateRequired(data, "name", "string", context);
        this.validateFieldType(data, "deactivated", "boolean", context, true);

        if (data.fields !== undefined) {
            this.validateArray(data.fields, (item, ctx) => {
                this.validateIFlexibleField(item, ctx);
            }, "fields", context);
        }

        if (data.attributeSets !== undefined) {
            this.validateArray(data.attributeSets, (item, ctx) => {
                this.validateIFlexibleFieldSet(item, ctx);
            }, "attributeSets", context);
        }

        if (data.nonStandards !== undefined) {
            this.validateArray(data.nonStandards, (item, ctx) => {
                this.validateINonStandard(item, ctx, false);
            }, "nonStandards", context);
        }

        this.validateStrictObjectFields(
            data,
            ["id", "name", "deactivated", "fields", "attributeSets", "nonStandards", ...additionalAllowedFields],
            context
        );
    }

    // ============= Nested Type Validators =============

    /**
     * Validate IPlan
     */
    private validateIPlan(data: any, context: ValidationContext): void {
        const savedPath = context.path;
        try {
            this.validateRequired(data, "id", "string", context);
            this.validateRequired(data, "name", "string", context);
            this.validateFieldType(data, "billingOnlyPlan", "boolean", context, true);
            this.validateFieldType(data, "deactivated", "boolean", context, true);

            if (data.feesAndPremiumsRef !== undefined && data.feesAndPremiumsRef !== null) {
                this.validateIRef(data.feesAndPremiumsRef, "feesAndPremiumsRef", context);
            }

            if (data.grandFathered !== undefined && data.grandFathered !== null) {
                this.validateIOptionSetValue(data.grandFathered, context, "grandFathered");
            }

            if (data.fundingType !== undefined && data.fundingType !== null) {
                this.validateIOptionSetValue(data.fundingType, context, "fundingType");
            }

            if (data.fields !== undefined) {
                this.validateArray(data.fields, (item, ctx) => {
                    this.validateIFlexibleField(item, ctx);
                }, "fields", context);
            }

            if (data.attributeSets !== undefined) {
                this.validateArray(data.attributeSets, (item, ctx) => {
                    this.validateIFlexibleFieldSet(item, ctx);
                }, "attributeSets", context);
            }

            if (data.nonStandards !== undefined) {
                this.validateArray(data.nonStandards, (item, ctx) => {
                    this.validateINonStandard(item, ctx, false);
                }, "nonStandards", context);
            }

            if (data.benefitRefs !== undefined) {
                this.validateArray(data.benefitRefs, (item, ctx) => {
                    this.validateIRef(item, "benefitRefs", ctx);
                }, "benefitRefs", context);
            }

            this.validateStrictObjectFields(
                data,
                [
                    "id", "name", "feesAndPremiumsRef", "grandFathered", "fields", "attributeSets",
                    "nonStandards", "benefitRefs", "fundingType", "billingOnlyPlan", "deactivated"
                ],
                context
            );
        } finally {
            context.path = savedPath;
        }
    }

    /**
     * Validate IBenefit
     */
    private validateIBenefit(data: any, context: ValidationContext): void {
        const savedPath = context.path;
        try {
            const benefitModelFields = [
                "id", "name", "deactivated", "fields", "attributeSets", "nonStandards",
                "tiers", "tierSetRef", "grandFathered", "status", "product", "fundingType"
            ];

            const benefitCrmFields = [
                "bcbs_istierenabled", "bcbs_isgrandfatherenabled", "bcbs_isreleased", "bcbs_sortorder",
                "_bcbs_copaycoinsurancesetid_value", "versionnumber", "bcbs_config_categoryid", "bcbs_name",
                "statecode", "_bcbs_supercategoryid_value", "bcbs_isfundingtypeenabled",
                "_bcbs_config_segmentid_value", "statuscode", "bcbs_helptext", "bcbs_isenablenonstandard"
            ];


            // TODO 
            // following fields need to be removed
            const benefitMetadataFields = [
                "_owningbusinessunit_value", "_createdby_value", "modifiedon", "_owninguser_value",
                "_modifiedby_value", "createdon", "_ownerid_value", "_modifiedonbehalfby_value",
                "utcconversiontimezonecode", "bcbs_importkey", "overriddencreatedon",
                "timezoneruleversionnumber", "importsequencenumber", "_owningteam_value",
                "_createdonbehalfby_value", "@odata.etag"
            ];

            // Base category validation
            this.validateICategoryBase(data, context, [...benefitModelFields, ...benefitCrmFields, ...benefitMetadataFields]);

            // Benefit-specific fields
            if (data.tiers !== undefined) {
                this.validateArray(data.tiers, (item, ctx) => {
                    this.validateITierLevel(item, ctx);
                }, "tiers", context);
            }

            if (data.tierSetRef !== undefined && data.tierSetRef !== null) {
                this.validateIRef(data.tierSetRef, "tierSetRef", context);
            }

            if (data.status !== undefined && data.status !== null) {
                this.validateIOptionSetValue(data.status, context, "status");
            }

            if (data.product !== undefined && data.product !== null) {
                this.validateIOptionSetValue(data.product, context, "product");
            }

            if (data.fundingType !== undefined && data.fundingType !== null) {
                this.validateIOptionSetValue(data.fundingType, context, "fundingType");
            }

            if (data.grandFathered !== undefined && data.grandFathered !== null) {
                this.validateIOptionSetValue(data.grandFathered, context, "grandFathered");
            }

            this.validateStrictObjectFields(
                data,
                [...benefitModelFields, ...benefitCrmFields, ...benefitMetadataFields],
                context
            );
        } finally {
            context.path = savedPath;
        }
    }

    /**
     * Validate IFlexibleField
     */
    private validateIFlexibleField(data: any, context: ValidationContext): void {
        const savedPath = context.path;
        try {
            this.validateRequired(data, "id", "string", context);
            this.validateRequired(data, "name", "string", context);
            this.validateRequired(data, "masterAttributeID", "string", context);

            if (data.configRef !== undefined && data.configRef !== null) {
                this.validateIRef(data.configRef, "configRef", context);
            }

            if (data.visibility !== undefined && data.visibility !== null) {
                this.validateFieldType(data, "visibility", "number", context, true);
            }

            if (data.values !== undefined) {
                this.validateArray(data.values, (item, ctx) => {
                    this.validateIFlexibleFieldValue(item, ctx);
                }, "values", context);
            }

            this.validateStrictObjectFields(
                data,
                ["id", "name", "masterAttributeID", "configRef", "values", "visibility"],
                context
            );
        } finally {
            context.path = savedPath;
        }
    }

    /**
     * Validate IFlexibleFieldValue
     */
    private validateIFlexibleFieldValue(data: any, context: ValidationContext): void {
        const savedPath = context.path;
        try {
            this.validateRequired(data, "id", "string", context);
            this.validateRequired(data, "type", "string", context);

            const validTypes = ["primary", "pcp", "specialist"];
            if (!validTypes.includes(data.type)) {
                this.addError("type", `Invalid field type. Expected one of: ${validTypes.join(", ")}`, context);
            }

            if (data.tierRef !== undefined && data.tierRef !== null) {
                this.validateIRef(data.tierRef, "tierRef", context);
            }

            if (data.value !== undefined && data.value !== null) {
                this.validateTValue(data.value, context);
            }

            if (data.emitRefId !== undefined && data.emitRefId !== null) {
                this.validateFieldType(data, "emitRefId", "string", context, true);
            }

            this.validateStrictObjectFields(data, ["id", "tierRef", "type", "value", "emitRefId"], context);
        } finally {
            context.path = savedPath;
        }
    }

    /**
     * Validate IFlexibleFieldSet
     */
    private validateIFlexibleFieldSet(data: IFlexibleFieldSet, context: ValidationContext): void {
        const savedPath = context.path;
        try {
            this.validateRequired(data, "id", "string", context);
            this.validateRequired(data, "name", "string", context);
            this.validateFieldType(data, "deactivated", "boolean", context, true);

            if (data.attributeSetRef !== undefined && data.attributeSetRef !== null) {
                this.validateIRef(data.attributeSetRef, "attributeSetRef", context);
            }

            if (data.fields !== undefined) {
                this.validateArray(data.fields, (item, ctx) => {
                    this.validateIFlexibleField(item, ctx);
                }, "fields", context);
            }

            if (data.dependecy !== undefined && data.dependecy !== null) {
                if (typeof data.dependecy === "object" && data.dependecy.originRef !== undefined) {
                    this.validateIRef(data.dependecy.originRef, "dependecy.originRef", context);
                }

                this.validateStrictObjectFields(data.dependecy, ["originRef"], context, "dependecy");
            }

            this.validateStrictObjectFields(
                data,
                ["id", "name", "attributeSetRef", "fields", "deactivated", "dependecy"],
                context
            );
        } finally {
            context.path = savedPath;
        }
    }

    /**
     * Validate INonStandard
     */
    private validateINonStandard(data: INonStandard, context: ValidationContext, isTemplate: boolean): void {
        const savedPath = context.path;
        try {
            this.validateRequired(data, "id", "string", context);
            this.validateRequired(data, "name", "string", context);
            this.validateFieldType(data, "deactivated", "boolean", context, true);
            this.validateFieldType(data, "referenceOnly", "boolean", context, true);
            this.validateFieldType(data, "partOfTemplate", "boolean", context, true);

            if (data.sectionRef !== undefined && data.sectionRef !== null) {
                this.validateIRef(data.sectionRef, "sectionRef", context);
            }

            if (data.fields !== undefined) {
                this.validateArray(data.fields, (item, ctx) => {
                    this.validateIFlexibleField(item, ctx);
                }, "fields", context);
            }

            const allowedFields = [
                "id", "name", "sectionRef", "fields", "deactivated", "referenceOnly", "partOfTemplate",
            ];


            // TODO 
            // "attributeSetRef" needs to be removed
            allowedFields.push("attributeSetRef", "dependecy");

            if (isTemplate) {
                allowedFields.push("linkedCategoryTypeId", "linkedCategoryName", "relationships");
            }

            this.validateStrictObjectFields(data, allowedFields, context);
        } finally {
            context.path = savedPath;
        }
    }

    /**
     * Validate INonStandardTemplate
     */
    private validateINonStandardTemplate(data: INonStandardTemplate, context: ValidationContext): void {
        const savedPath = context.path;
        try {
            // Base non-standard validation
            this.validateINonStandard(data, context, true);

            if (data.linkedCategoryTypeId !== undefined && data.linkedCategoryTypeId !== null) {
                this.validateFieldType(data, "linkedCategoryTypeId", "string", context, true);
            }

            if (data.linkedCategoryName !== undefined && data.linkedCategoryName !== null) {
                this.validateFieldType(data, "linkedCategoryName", "string", context, true);
            }

            if (data.relationships !== undefined) {
                this.validateArray(data.relationships, (item, ctx) => {
                    this.validateITemplateRelationship(item, ctx);
                }, "relationships", context);
            }

            this.validateStrictObjectFields(
                data,
                [
                    "id", "name", "sectionRef", "fields", "deactivated", "referenceOnly", "partOfTemplate",
                    "linkedCategoryTypeId", "linkedCategoryName", "relationships"
                ],
                context
            );
        } finally {
            context.path = savedPath;
        }
    }

    /**
     * Validate ITemplateRelationship
     */
    private validateITemplateRelationship(data: any, context: ValidationContext): void {
        const savedPath = context.path;
        try {
            this.validateRequired(data, "categoryID", "string", context);
            this.validateRequired(data, "categoryType", "string", context);

            if (data.sectionRefs !== undefined) {
                this.validateArray(data.sectionRefs, (item, ctx) => {
                    this.validateITemplateSectionRelationship(item, ctx);
                }, "sectionRefs", context);
            }

            this.validateStrictObjectFields(data, ["categoryID", "categoryType", "sectionRefs"], context);
        } finally {
            context.path = savedPath;
        }
    }

    /**
     * Validate ITemplateSectionRelationship
     */
    private validateITemplateSectionRelationship(data: any, context: ValidationContext): void {
        const savedPath = context.path;
        try {
            this.validateRequired(data, "sectionId", "string", context);

            if (data.nonStandards !== undefined) {
                this.validateArray(data.nonStandards, (item, ctx) => {
                    this.validateIRef(item, "nonStandards", ctx);
                }, "nonStandards", context);
            }

            this.validateStrictObjectFields(data, ["sectionId", "nonStandards"], context);
        } finally {
            context.path = savedPath;
        }
    }

    /**
     * Validate IGroupStructure
     */
    private validateIGroupStructure(data: IGroupStructure, context: ValidationContext): void {
        const savedPath = context.path;
        try {
            context.path = `${savedPath}.groupStructure`;

            if (data.groupNumbers !== undefined) {
                this.validateArray(data.groupNumbers, (item, ctx) => {
                    this.validateIGroupNumber(item, ctx);
                }, "groupNumbers", context);
            }

            if (data.departments !== undefined) {
                this.validateArray(data.departments, (item, ctx) => {
                    this.validateIDepartment(item, ctx);
                }, "departments", context);
            }

            if (data.cesNumbers !== undefined) {
                this.validateArray(data.cesNumbers, (item, ctx) => {
                    this.validateICESNumber(item, ctx);
                }, "cesNumbers", context);
            }

            this.validateStrictObjectFields(data, ["groupNumbers", "departments", "cesNumbers"], context);
        } finally {
            context.path = savedPath;
        }
    }

    /**
     * Validate IGroupNumber
     */
    private validateIGroupNumber(data: IGroupNumber, context: ValidationContext): void {
        const savedPath = context.path;
        try {
            this.validateRequired(data, "id", "string", context);
            this.validateFieldType(data, "deactivated", "boolean", context, true);

            if (data.prefix !== undefined && data.prefix !== null) {
                this.validateFieldType(data, "prefix", "string", context, true);
            }
            if (data.base !== undefined && data.base !== null) {
                this.validateFieldType(data, "base", "string", context, true);
            }
            if (data.suffix !== undefined && data.suffix !== null) {
                this.validateFieldType(data, "suffix", "string", context, true);
            }
            if (data.groupNumber !== undefined && data.groupNumber !== null) {
                this.validateFieldType(data, "groupNumber", "string", context, true);
            }

            if (data.cesNumberRef !== undefined && data.cesNumberRef !== null) {
                this.validateIRef(data.cesNumberRef, "cesNumberRef", context);
            }
            if (data.planRef !== undefined && data.planRef !== null) {
                this.validateIRef(data.planRef, "planRef", context);
            }
            if (data.stopLossRef !== undefined && data.stopLossRef !== null) {
                this.validateIRef(data.stopLossRef, "stopLossRef", context);
            }
            if (data.accountsReceivableRef !== undefined && data.accountsReceivableRef !== null) {
                this.validateIRef(data.accountsReceivableRef, "accountsReceivableRef", context);
            }

            if (data.fields !== undefined) {
                this.validateArray(data.fields, (item, ctx) => {
                    this.validateIFlexibleField(item, ctx);
                }, "fields", context);
            }

            if (data.status !== undefined && data.status !== null) {
                this.validateIOptionSetValue(data.status, context, "status");
            }

            this.validateStrictObjectFields(
                data,
                [
                    "id", "cesNumberRef", "prefix", "base", "suffix", "groupNumber", "fields",
                    "accountsReceivableRef", "planRef", "stopLossRef", "deactivated", "status"
                ],
                context
            );
        } finally {
            context.path = savedPath;
        }
    }

    /**
     * Validate IDepartment
     */
    private validateIDepartment(data: IDepartment, context: ValidationContext): void {
        const savedPath = context.path;
        try {
            this.validateRequired(data, "id", "string", context);
            this.validateFieldType(data, "deactivated", "boolean", context, true);

            if (data.name !== undefined && data.name !== null) {
                this.validateFieldType(data, "name", "string", context, true);
            }
            if (data.description !== undefined && data.description !== null) {
                this.validateFieldType(data, "description", "string", context, true);
            }

            if (data.cesNumberRef !== undefined && data.cesNumberRef !== null) {
                this.validateIRef(data.cesNumberRef, "cesNumberRef", context);
            }

            this.validateStrictObjectFields(data, ["id", "name", "description", "cesNumberRef", "deactivated"], context);
        } finally {
            context.path = savedPath;
        }
    }

    /**
     * Validate ICESNumber
     */
    private validateICESNumber(data: ICESNumber, context: ValidationContext): void {
        const savedPath = context.path;
        try {
            this.validateRequired(data, "id", "string", context);
            this.validateRequired(data, "cesNumber", "string", context);
            this.validateFieldType(data, "deactivated", "boolean", context, true);

            this.validateStrictObjectFields(data, ["id", "cesNumber", "deactivated"], context);
        } finally {
            context.path = savedPath;
        }
    }

    /**
     * Validate IComment
     */
    private validateIComment(data: IComment, context: ValidationContext): void {
        const savedPath = context.path;
        try {
            this.validateRequired(data, "id", "string", context);
            this.validateRequired(data, "comment", "string", context);
            this.validateRequired(data, "status", "number", context);

            const validStatuses = [ECommentStatus.New, ECommentStatus.Resolved];
            if (!validStatuses.includes(data.status)) {
                this.addError("status", `Invalid comment status. Expected ${validStatuses.join(" or ")}`, context);
            }

            if (data.location !== undefined && data.location !== null) {
                // RouterParams is just an object, minimal validation
                if (typeof data.location !== "object" || Array.isArray(data.location)) {
                    this.addError("location", "location must be an object", context);
                }
            }

            this.validateStrictObjectFields(data, ["id", "comment", "location", "status"], context);
        } finally {
            context.path = savedPath;
        }
    }

    /**
     * Validate INotification
     */
    private validateINotification(data: INotification, context: ValidationContext): void {
        const savedPath = context.path;
        try {
            this.validateRequired(data, "id", "string", context);
            this.validateRequired(data, "name", "string", context);

            if (data.title !== undefined && data.title !== null) {
                this.validateFieldType(data, "title", "string", context, true);
            }
            if (data.description !== undefined && data.description !== null) {
                this.validateFieldType(data, "description", "string", context, true);
            }
            if (data.deactivated !== undefined && data.deactivated !== null) {
                this.validateFieldType(data, "deactivated", "boolean", context, true);
            }

            if (data.location !== undefined && data.location !== null) {
                if (typeof data.location !== "object" || Array.isArray(data.location)) {
                    this.addError("location", "location must be an object", context);
                }
            }

            this.validateStrictObjectFields(
                data,
                ["id", "name", "title", "description", "deactivated", "location", "regarding"],
                context
            );
        } finally {
            context.path = savedPath;
        }
    }

    /**
     * Validate IAccountsReceivable (extends ICategoryBase)
     */
    private validateIAccountsReceivable(data: IAccountsReceivable, context: ValidationContext): void {
        const savedPath = context.path;
        try {
            this.validateICategoryBase(data, context);

            this.validateStrictObjectFields(
                data,
                ["id", "name", "deactivated", "fields", "attributeSets", "nonStandards"],
                context
            );
        } finally {
            context.path = savedPath;
        }
    }

    /**
     * Validate IFeesAndPremiums (extends ICategoryBase)
     */
    private validateIFeesAndPremiums(data: IFeesAndPremiums, context: ValidationContext): void {
        const savedPath = context.path;
        try {
            this.validateICategoryBase(data, context);

            if (data.stopLossRef !== undefined && data.stopLossRef !== null) {
                this.validateIRef(data.stopLossRef, "stopLossRef", context);
            }

            this.validateStrictObjectFields(
                data,
                ["id", "name", "deactivated", "fields", "attributeSets", "nonStandards", "stopLossRef"],
                context
            );
        } finally {
            context.path = savedPath;
        }
    }

    /**
     * Validate IStopLoss (extends ICategoryBase)
     */
    private validateIStopLoss(data: IStopLoss, context: ValidationContext): void {
        const savedPath = context.path;
        try {
            this.validateICategoryBase(data, context);

            this.validateStrictObjectFields(
                data,
                ["id", "name", "deactivated", "fields", "attributeSets", "nonStandards"],
                context
            );
        } finally {
            context.path = savedPath;
        }
    }

    /**
     * Validate IPharmacyORE
     */
    private validateIPharmacyORE(data: IPharmacyORE, context: ValidationContext): void {
        const savedPath = context.path;
        try {
            this.validateRequired(data, "id", "string", context);
            this.validateRequired(data, "releaseStatus", "string", context);
            this.validateRequired(data, "pharmacyPlanDesign", "string", context);
            this.validateRequired(data, "newPharmacyPlanDesign", "string", context);
            this.validateRequired(data, "newClientNumber", "string", context);

            const validStatuses = ["In Progress", "Ready for Release"];
            if (!validStatuses.includes(data.releaseStatus)) {
                this.addError("releaseStatus", `Invalid releaseStatus. Expected one of: ${validStatuses.join(", ")}`, context);
            }

            if (data.releaseDate !== undefined && data.releaseDate !== null) {
                this.validateIDateValue(data.releaseDate, context, "releaseDate");
            }

            this.validateStrictObjectFields(
                data,
                ["id", "releaseStatus", "releaseDate", "pharmacyPlanDesign", "newPharmacyPlanDesign", "newClientNumber"],
                context
            );
        } finally {
            context.path = savedPath;
        }
    }

    /**
     * Validate ITierLevel
     */
    private validateITierLevel(data: ITierLevel, context: ValidationContext): void {
        const savedPath = context.path;
        try {
            this.validateRequired(data, "id", "string", context);
            this.validateRequired(data, "name", "string", context);
            this.validateFieldType(data, "description", "string", context, true);
            this.validateFieldType(data, "pcp", "boolean", context, true);
            this.validateFieldType(data, "ion", "boolean", context, true);
            this.validateFieldType(data, "isLabelDisabled", "boolean", context, true);

            const tierModelFields = ["id", "name", "description", "pcp", "ion", "isLabelDisabled"];
            const tierCrmFields = [
                "statecode", "bcbs_reportingname", "bcbs_config_tierlevelid", "bcbs_isallownameupdate",
                "bcbs_name", "_bcbs_tiersetid_value", "bcbs_sortorder", "statuscode",
                "bcbs_isenablepcpspecialist", "bcbs_isreleased", "versionnumber"
            ];


            // TODO
            // tier meta data fields also need to be removed
            const tierMetadataFields = [
                "_owningbusinessunit_value", "_createdby_value", "modifiedon", "_owninguser_value",
                "_modifiedby_value", "createdon", "_ownerid_value", "_modifiedonbehalfby_value",
                "utcconversiontimezonecode", "overriddencreatedon", "timezoneruleversionnumber",
                "importsequencenumber", "_owningteam_value", "_createdonbehalfby_value", "@odata.etag"
            ];

            this.validateStrictObjectFields(data, [...tierModelFields, ...tierCrmFields, ...tierMetadataFields], context);
        } finally {
            context.path = savedPath;
        }
    }

    // ============= Value Type Validators =============

    /**
     * Validate TValue (discriminated union of all value types)
     */
    private validateTValue(value: TValue, context: ValidationContext): void {
        if (value === null || value === undefined) {
            return; // TValue includes null
        }

        if (!value.type) {
            this.addError("value.type", "Value object must have a 'type' discriminator", context);
            return;
        }

        switch (value.type) {
            case "string":
                this.validateIStringValue(value, context);
                break;
            case "number":
                this.validateINumberValue(value, context);
                break;
            case "entity":
                this.validateIEntityReferenceValue(value, context);
                break;
            case "bool":
                this.validateIBooleanValue(value, context);
                break;
            case "optionset":
                this.validateIOptionSetValue(value, context);
                break;
            case "date":
                this.validateIDateValue(value, context);
                break;
            case "multioptionset":
                this.validateIMultiselectOptionSetValue(value, context);
                break;
            default:
                this.addError("value.type", `Unknown value type: ${(value as any).type}`, context);
        }
    }

    /**
     * Validate IStringValue
     */
    private validateIStringValue(value: IStringValue, context: ValidationContext): void {
        if (value.type !== "string") {
            this.addError("value.type", `Expected type "string", got "${value.type}"`, context);
        }
        if (value.value !== undefined && typeof value.value !== "string") {
            this.addError("value.value", `Expected string value, got ${typeof value.value}`, context);
        }

        this.validateStrictObjectFields(value, ["type", "value"], context);
    }

    /**
     * Validate INumberValue
     */
    private validateINumberValue(value: INumberValue, context: ValidationContext): void {
        if (value.type !== "number") {
            this.addError("value.type", `Expected type "number", got "${value.type}"`, context);
        }
        if (value.value !== undefined && typeof value.value !== "number") {
            this.addError("value.value", `Expected number value, got ${typeof value.value}`, context);
        }

        this.validateStrictObjectFields(value, ["type", "value"], context);
    }

    /**
     * Validate IDateValue
     */
    private validateIDateValue(value: IDateValue, context: ValidationContext, fieldName: string = "value"): void {
        if (value.type !== "date" && fieldName === "value") {
            this.addError(`${fieldName}.type`, `Expected type "date", got "${value.type}"`, context);
        }
        if (value.value !== undefined && typeof value.value !== "string") {
            this.addError(`${fieldName}.value`, `Expected string date value, got ${typeof value.value}`, context);
        }

        this.validateStrictObjectFields(value, ["type", "value"], context);
    }

    /**
     * Validate IBooleanValue
     */
    private validateIBooleanValue(value: IBooleanValue, context: ValidationContext, fieldName: string = "value"): void {
        if (value.type !== "bool" && fieldName === "value") {
            this.addError(`${fieldName}.type`, `Expected type "bool", got "${value.type}"`, context);
        }
        if (value.value !== undefined && typeof value.value !== "boolean") {
            this.addError(`${fieldName}.value`, `Expected boolean value, got ${typeof value.value}`, context);
        }

        this.validateStrictObjectFields(value, ["type", "value"], context);
    }

    /**
     * Validate IOptionSetValue
     */
    private validateIOptionSetValue(value: IOptionSetValue, context: ValidationContext, fieldName: string = "value"): void {
        if (value.type !== "optionset" && fieldName === "value") {
            this.addError(`${fieldName}.type`, `Expected type "optionset", got "${value.type}"`, context);
        }
        if (typeof value.name !== "string") {
            this.addError(`${fieldName}.name`, `Expected string name, got ${typeof value.name}`, context);
        }
        if (value.value !== undefined && typeof value.value !== "number" && typeof value.value !== "string") {
            this.addError(`${fieldName}.value`, `Expected number or string value, got ${typeof value.value}`, context);
        }

        this.validateStrictObjectFields(value, ["type", "name", "value"], context, fieldName !== "value" ? fieldName : undefined);
    }

    /**
     * Validate IEntityReferenceValue
     */
    private validateIEntityReferenceValue(value: IEntityReferenceValue, context: ValidationContext, fieldName: string = "value"): void {
        if (typeof value !== "object" || Array.isArray(value) || value === null) {
            this.addError(fieldName, "Expected object for entity reference", context);
            return;
        }

        if (!value.id || typeof value.id !== "string") {
            this.addError(`${fieldName}.id`, "Entity reference must have a non-empty string id", context);
        }
        if (!value.name || typeof value.name !== "string") {
            this.addError(`${fieldName}.name`, "Entity reference must have a non-empty string name", context);
        }
        if (value.type !== "entity" && fieldName === "value") {
            this.addError(`${fieldName}.type`, `Expected type "entity", got "${value.type}"`, context);
        }
        if (!value.entityType || typeof value.entityType !== "string") {
            this.addError(`${fieldName}.entityType`, "Entity reference must have an entityType", context);
        }

        this.validateStrictObjectFields(value, ["type", "id", "name", "entityType"], context, fieldName !== "value" ? fieldName : undefined);
    }

    /**
     * Validate IMultiselectOptionSetValue
     */
    private validateIMultiselectOptionSetValue(value: IMultiselectOptionSetValue, context: ValidationContext, fieldName: string = "value"): void {
        if (value.type !== "multioptionset" && fieldName === "value") {
            this.addError(`${fieldName}.type`, `Expected type "multioptionset", got "${value.type}"`, context);
        }
        if (value.entityType !== "bcbs_config_optionsetitem") {
            this.addError(`${fieldName}.entityType`, `Expected "bcbs_config_optionsetitem", got "${value.entityType}"`, context);
        }
        if (value.value !== undefined && typeof value.value !== "string") {
            this.addError(`${fieldName}.value`, `Expected string value, got ${typeof value.value}`, context);
        }
        if (!Array.isArray(value.options)) {
            this.addError(`${fieldName}.options`, "Expected options to be an array", context);
        } else {
            value.options.forEach((option: any, index: number) => {
                if (!option.id || typeof option.id !== "string") {
                    this.addError(`${fieldName}.options[${index}].id`, "Option must have string id", context);
                }
                if (!option.name || typeof option.name !== "string") {
                    this.addError(`${fieldName}.options[${index}].name`, "Option must have string name", context);
                }
                if (typeof option.value !== "number") {
                    this.addError(`${fieldName}.options[${index}].value`, `Expected number, got ${typeof option.value}`, context);
                }

                this.validateStrictObjectFields(option, ["id", "name", "value"], context, `${fieldName}.options[${index}]`);
            });
        }

        this.validateStrictObjectFields(value, ["type", "entityType", "value", "options"], context, fieldName !== "value" ? fieldName : undefined);
    }

    /**
     * Validate IRef
     */
    private validateIRef(ref: IRef, fieldName: string, context: ValidationContext): void {
        if (typeof ref !== "object" || Array.isArray(ref) || ref === null) {
            this.addError(fieldName, "IRef must be an object", context);
            return;
        }

        if (!ref.id || typeof ref.id !== "string") {
            this.addError(`${fieldName}.id`, "IRef.id must be a non-empty string", context);
        }
        if (!ref.name || typeof ref.name !== "string") {
            this.addError(`${fieldName}.name`, "IRef.name must be a non-empty string", context);
        }

        this.validateStrictObjectFields(ref, ["id", "name", "bcbs_config_categoryid"], context, fieldName);

        // TODO 
        // "bcbs_config_categoryid" needs to be removed
    }

    // ============= Reference Integrity Validation =============

    /**
     * Build a map of all entities by ID for reference validation
     */
    private buildReferenceMap(data: IGroupConfigurationData, context: ValidationContext): void {
        const mapEntry = (entityType: string, entity: any) => {
            if (entity && entity.id) {
                const key = `${entityType}:${entity.id}`;
                context.refMap.set(key, entity.name || "");
            }
        };

        // Map all plans
        if (data.plans) {
            data.plans.forEach(plan => mapEntry("plan", plan));
        }

        // Map all benefits
        if (data.benefits) {
            data.benefits.forEach(benefit => mapEntry("benefit", benefit));
        }

        // Map all fees and premiums
        if (data.feesAndPremiums) {
            data.feesAndPremiums.forEach(fp => mapEntry("feesAndPremiums", fp));
        }

        // Map all stop loss
        if (data.stopLoss) {
            data.stopLoss.forEach(sl => mapEntry("stopLoss", sl));
        }

        // Map all AR
        if (data.claimsARs) {
            data.claimsARs.forEach(ar => mapEntry("accountsReceivable", ar));
        }

        // Map group structure entities
        if (data.groupStructure) {
            if (data.groupStructure.groupNumbers) {
                data.groupStructure.groupNumbers.forEach(gn => mapEntry("groupNumber", gn));
            }
            if (data.groupStructure.departments) {
                data.groupStructure.departments.forEach(dept => mapEntry("department", dept));
            }
            if (data.groupStructure.cesNumbers) {
                data.groupStructure.cesNumbers.forEach(ces => mapEntry("cesNumber", ces));
            }
        }

        // Map tier levels (nested in benefits or data)
        if (data.benefits) {
            data.benefits.forEach(benefit => {
                if (benefit.tiers) {
                    benefit.tiers.forEach(tier => mapEntry("tierLevel", tier));
                }
            });
        }
    }

    /**
     * Validate that all IRef references resolve to entities in the reference map
     */
    private validateReferences(data: IGroupConfigurationData, context: ValidationContext): void {
        // Validate feesAndPremiumsRef in plans
        if (data.plans) {
            data.plans.forEach((plan, index) => {
                if (plan.feesAndPremiumsRef) {
                    this.validateRefExists(plan.feesAndPremiumsRef, "feesAndPremiums", `plans[${index}].feesAndPremiumsRef`, context);
                }
                if (plan.benefitRefs) {
                    plan.benefitRefs.forEach((ref, refIndex) => {
                        this.validateRefExists(ref, "benefit", `plans[${index}].benefitRefs[${refIndex}]`, context);
                    });
                }
            });
        }

        // Validate benefit tiers and references
        if (data.benefits) {
            data.benefits.forEach((benefit, index) => {
                if (benefit.tierSetRef) {
                    // Note: tierSetRef points to tier set, which we don't have in the map
                    // This would require more context to validate
                }
            });
        }

        // Validate stop loss references in fees and premiums
        if (data.feesAndPremiums) {
            data.feesAndPremiums.forEach((fp, index) => {
                if (fp.stopLossRef) {
                    this.validateRefExists(fp.stopLossRef, "stopLoss", `feesAndPremiums[${index}].stopLossRef`, context);
                }
            });
        }

        // Validate group structure references
        if (data.groupStructure) {
            if (data.groupStructure.groupNumbers) {
                data.groupStructure.groupNumbers.forEach((gn, index) => {
                    if (gn.cesNumberRef) {
                        this.validateRefExists(gn.cesNumberRef, "cesNumber", `groupStructure.groupNumbers[${index}].cesNumberRef`, context);
                    }
                    if (gn.planRef) {
                        this.validateRefExists(gn.planRef, "plan", `groupStructure.groupNumbers[${index}].planRef`, context);
                    }
                    if (gn.stopLossRef) {
                        this.validateRefExists(gn.stopLossRef, "stopLoss", `groupStructure.groupNumbers[${index}].stopLossRef`, context);
                    }
                    if (gn.accountsReceivableRef) {
                        this.validateRefExists(gn.accountsReceivableRef, "accountsReceivable", `groupStructure.groupNumbers[${index}].accountsReceivableRef`, context);
                    }
                });
            }

            if (data.groupStructure.departments) {
                data.groupStructure.departments.forEach((dept, index) => {
                    if (dept.cesNumberRef) {
                        this.validateRefExists(dept.cesNumberRef, "cesNumber", `groupStructure.departments[${index}].cesNumberRef`, context);
                    }
                });
            }
        }
    }

    /**
     * Validate that a specific reference exists in the map
     */
    private validateRefExists(ref: IRef, entityType: string, path: string, context: ValidationContext): void {
        const key = `${entityType}:${ref.id}`;
        if (!context.refMap.has(key)) {
            const normalizedPath = path.startsWith("root.") ? path : `root.${path}`;
            this.addError(normalizedPath, `Referenced ${entityType} with id "${ref.id}" not found in data`, context);
        }
    }

    // ============= Helper Methods =============

    /**
     * Validate a required field exists and has the correct type
     */
    private validateRequired(data: any, fieldName: string, expectedType: string, context: ValidationContext): void {
        const savedPath = context.path;
        context.path = `${savedPath}.${fieldName}`;

        try {
            if (data[fieldName] === undefined || data[fieldName] === null) {
                this.addError(fieldName, `Required field "${fieldName}" is missing`, context);
                return;
            }

            const actualType = Array.isArray(data[fieldName]) ? "array" : typeof data[fieldName];
            if (actualType !== expectedType) {
                this.addError(fieldName, `Expected type "${expectedType}", got "${actualType}"`, context);
            }
        } finally {
            context.path = savedPath;
        }
    }

    /**
     * Validate a field has the correct type (field may be optional)
     */
    private validateFieldType(data: any, fieldName: string, expectedType: string, context: ValidationContext, isOptional: boolean = false): void {
        const savedPath = context.path;
        context.path = `${savedPath}.${fieldName}`;

        try {
            if (data[fieldName] === undefined || data[fieldName] === null) {
                if (!isOptional && data[fieldName] === undefined) {
                    this.addError(fieldName, `Required field "${fieldName}" is missing`, context);
                }
                return;
            }

            const actualType = Array.isArray(data[fieldName]) ? "array" : typeof data[fieldName];
            if (actualType !== expectedType) {
                this.addError(fieldName, `Expected type "${expectedType}", got "${actualType}"`, context);
            }
        } finally {
            context.path = savedPath;
        }
    }

    /**
     * Validate an array of items
     */
    private validateArray(
        array: any,
        itemValidator: (item: any, context: ValidationContext) => void,
        fieldName: string,
        context: ValidationContext
    ): void {
        const savedPath = context.path;
        const seenIds = new Map<string, number>();

        try {
            if (!Array.isArray(array)) {
                context.path = `${savedPath}.${fieldName}`;
                this.addError(fieldName, "Expected array", context);
                return;
            }

            array.forEach((item, index) => {
                context.path = `${savedPath}.${fieldName}[${index}]`;

                // Enforce id uniqueness within the current array scope.
                if (item && typeof item === "object" && !Array.isArray(item) && typeof item.id === "string") {
                    const id = item.id;
                    const existingIndex = seenIds.get(id);
                    if (existingIndex !== undefined) {
                        this.addError(
                            "id",
                            `Duplicate id "${id}" in scope "${fieldName}"; first occurrence at index ${existingIndex}`,
                            context
                        );
                    } else {
                        seenIds.set(id, index);
                    }
                }

                itemValidator(item, context);
            });
        } finally {
            context.path = savedPath;
        }
    }

    /**
     * Validate no extra fields exist (strict mode)
     */
    private validateNoExtraFields(data: any, allowedFields: string[], context: ValidationContext): void {
        const allowedSet = new Set(allowedFields);
        for (const key of Object.keys(data)) {
            if (!allowedSet.has(key)) {
                this.addError(key, `Unexpected attribute "${key}" not in schema`, context);
            }
        }
    }

    /**
     * Strict-mode wrapper for unknown field detection.
     */
    private validateStrictObjectFields(
        data: any,
        allowedFields: string[],
        context: ValidationContext,
        objectPath?: string
    ): void {
        if (!context.options.strict) {
            return;
        }

        if (!data || typeof data !== "object" || Array.isArray(data)) {
            return;
        }

        if (!objectPath) {
            this.validateNoExtraFields(data, allowedFields, context);
            return;
        }

        const savedPath = context.path;
        context.path = `${savedPath}.${objectPath}`;

        try {
            this.validateNoExtraFields(data, allowedFields, context);
        } finally {
            context.path = savedPath;
        }
    }

    /**
     * Add an error to the validation context
     */
    private addError(fieldPath: string, message: string, context: ValidationContext): void {
        const fullPath = context.path === "root" ? fieldPath : `${context.path}.${fieldPath}`;

        context.errors.push({
            title: `Validation error at ${fullPath}`,
            type: EValidationSeverity.error,
            description: message,
            value: fullPath,
        });
    }
}

export default GroupConfigurationSchemaValidator;
